import {Component, computed, OnInit, signal, Signal} from '@angular/core';
import {errorText} from '../../../graphql/error-link';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {combineLatest, of} from 'rxjs';
import {Apollo} from 'apollo-angular';
import {CombinedGraphQLErrors} from '@apollo/client/errors';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  shareReplay,
  skipWhile,
  startWith,
  switchMap,
  tap
} from 'rxjs/operators';
import {
  Airline,
  Mutation as WorkflowMutation,
  PoItem,
  PoItemPreview,
  ProcessStateEnum,
  Query
} from '../../../types-workflow';
import gql from 'graphql-tag';
import {PUBLISH_PO_ITEM} from '../../../queries-workflow';
import {BreadCrumb, LogoAreaComponent} from '../../logo-area/logo-area.component';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Organization} from '../../../types-repository';
import {HeadlineSectionComponent} from '../../headline-section/headline-section.component';
import {ClTabsModule} from '../../cl-tabs/cl-tabs.module';

import {PlayerComponent} from '../player/player.component';
import {VideoLadderComponent} from '../video-ladder/video-ladder.component';
import {
  FlytedgeMetadataPreviewComponent
} from '../../ifes/flytedge-metadata-preview/flytedge-metadata-preview.component';
import {FlytedgeMetadataRawComponent} from '../../ifes/flytedge-metadata-raw/flytedge-metadata-raw.component';
import {ContextService} from '../../../services/context.service';
import {Dialog} from '@angular/cdk/dialog';
import {ProcessService} from '../../../services/process.service';
import {BannerService} from '../../../banner/banner.service';
import {ConfirmDialogComponent} from '../../../confirm-dialog/confirm-dialog.component';
import {PO_DESTINATIONS} from '../../../target_systems';
import {toObservable, toSignal} from '@angular/core/rxjs-interop';


// Named per organization, like the other org-scoped queries: the cache memoizes a read by document and
// variables, so one shared document handed every organization the first one's PO item (VTK-3591).
const PREVIEW_QUERY = (org_id: string) => gql`query PreviewQuery${org_id.replace(/:/g, '_')}($po_name: String!, $po_item_id: String!) {
  list_POs(input: { filter: { po_name: { eq: $po_name}}}) {
    pos {
      id
      po_name
      airline
      poitems(po_item_id: $po_item_id) {
        id
        po_item_id
        filename
        po_destination
        po {
          po_name
          airline
        }
        publish_process {
          id
          state
          message
          start_date
          end_date
        }
        preview {
          dash_manifest_last_modified
          dash_manifest_url
          hls_manifest_url
          hls_manifest_last_modified
          drmtoday_token
          access_token
          ab_token
        }
      }
    }
  }
}
`

/**
 * The manifest the preview plays -- DASH when there is one, else HLS -- or null when there is none.
 *
 * Two properties, deliberately: the resolver returns whichever manifests exist in the preview bucket,
 * and the Flytedge feed sits next to either. Both are null once the preview has expired (the bucket's
 * lifecycle deleted it) or before it was ever generated; the resolver cannot tell those apart.
 */
export function previewManifestUrl(preview: PoItemPreview | null | undefined): string | null {
  return preview?.dash_manifest_url ?? preview?.hls_manifest_url ?? null;
}

@Component({
  selector: 'app-preview',
  templateUrl: './preview-poitem.component.html',
  styleUrls: ['./preview-poitem.component.css'],
  imports: [
    HeadlineSectionComponent,
    LogoAreaComponent,
    ClTabsModule,
    PlayerComponent,
    VideoLadderComponent,
    FlytedgeMetadataPreviewComponent,
    FlytedgeMetadataRawComponent,
    RouterLink
]
})
export class PreviewPoitemComponent implements OnInit {
  /** Why the encode could not be loaded at all (a preview-only error has its own banner). */
  loadError = signal<string | null>(null);
  /** The organization the shown PO item was loaded as; a Publish of it is sent as that organization. */
  readonly itemOrganizationId = signal<string | null>(null);
  /**
   * Publish waits while the shell's organization() is not yet the item's: the shell, the links and every
   * request without its own header still speak for the organization being left (VTK-3591).
   */
  readonly publishBlocked = computed(() =>
    !this.itemOrganizationId() || this.itemOrganizationId() !== this.organization()?.id);


  poItem: Signal<PoItem | undefined>;
  preview: Signal<PoItemPreview | undefined>;
  /** False while the PO item has no playable manifest: the page then shows why instead of a player. */
  hasManifest: Signal<boolean>;
  po_item_id: Signal<string | undefined>;
  filename: Signal<string | undefined>;
  flytedgeMetadataUrl: Signal<string | null | undefined>;
  breadcrumbs: Signal<BreadCrumb[]>;

  airline: Signal<Airline | null>;
  organization: Signal<Organization | null>;

  protected readonly PO_DESTINATIONS = PO_DESTINATIONS;
  publish_single_po_inflight = false;

  constructor(
    private route: ActivatedRoute,
    private apollo: Apollo,
    contextService: ContextService,
    private dialog: Dialog,
    private processService: ProcessService,
    private banner: BannerService,
    private http: HttpClient,
  ) {

    this.airline = contextService.airline;
    this.organization = contextService.organization;

    // Get route params as observables
    const po_name$ = this.route.params.pipe(
      map(p => p['po']),
      distinctUntilChanged()
    );

    const po_item_id$ = this.route.params.pipe(
      map(p => p['poitem']),
      distinctUntilChanged()
    );

    // The route's organization, once the context has caught up with it. An org switch on this page
    // keeps po and poitem, so without it the previous organization's PO item stayed on screen; and
    // until NavigationEnd the cache still keys list_POs on the organization being left (VTK-3591).
    const routeOrg$ = combineLatest(this.route.pathFromRoot.map(r => r.params)).pipe(
      map(ps => Object.assign({}, ...ps)['organization_id'] as string),
      distinctUntilChanged(),
    );
    // One key for everything the PO item depends on. Combined before gating, so a navigation that changes
    // the organization and po/poitem together can never pair the new PO item with the old organization.
    const key$ = combineLatest([routeOrg$, po_name$, po_item_id$, toObservable(contextService.organizationId)]).pipe(
      distinctUntilChanged((a, b) => a.every((v, n) => v === b[n])),
    );

    // Query for PO item data. undefined until (and unless) the current key's PO item arrives: every key
    // change clears the previous item first, and a refusal, an error or no PO leave it cleared -- so no
    // other organization's player or Publish link stays on screen.
    const poItemData$ = key$.pipe(
      switchMap(([organizationId, po_name, po_item_id, contextOrg]) => {
        this.loadError.set(null);
        this.itemOrganizationId.set(null);
        // Until the context has caught up, the cache would still key on the organization being left.
        if (!organizationId || organizationId !== contextOrg) {
          return of(undefined);
        }
        return this.apollo.use('workflow').watchQuery<Query>({
          query: PREVIEW_QUERY(organizationId),
          errorPolicy: 'all',
          variables: { po_name, po_item_id },
          context: {headers: new HttpHeaders({'X-Castlabs-Organization': organizationId})},
        }).valueChanges.pipe(
          // A thrown error (network) becomes a result with an error, so the stream lives on.
          catchError(error => of({data: undefined, error} as any)),
          tap(result => {
            if (result.data?.list_POs?.pos?.[0]?.poitems?.[0]) {
              this.loadError.set(null);
            } else if (result.error) {
              this.loadError.set(errorText(result.error));
            } else if (result.data) {
              this.loadError.set(`PO item ${po_item_id} of ${po_name} was not found.`);
            }
            if (CombinedGraphQLErrors.is(result.error)) {
              const previewError = result.error.errors.find(e => e.path?.includes('preview'));
              if (previewError) {
                this.banner.open(`Preview unavailable: ${previewError.message}`, ['Dismiss']);
              }
            }
          }),
          map(result => (result.data?.list_POs?.pos?.[0]?.poitems?.[0] ?? undefined) as PoItem | undefined),
          tap(item => this.itemOrganizationId.set(item ? organizationId : null)),
          startWith(undefined),
        );
      }),
      shareReplay(1)
    );

    // Convert to signals
    this.poItem = toSignal(poItemData$);

    this.preview = computed(() => {
      const poItem = this.poItem();
      return poItem?.preview;
    });

    this.hasManifest = computed(() => previewManifestUrl(this.preview()) != null);

    this.po_item_id = computed(() => {
      const poItem = this.poItem();
      return poItem?.po_item_id;
    });

    this.filename = computed(() => {
      const poItem = this.poItem();
      return poItem?.filename;
    });

    // Flytedge metadata URL - needs to stay as observable for HTTP call, then convert to signal
    const flytedgeMetadataUrl$ = poItemData$.pipe(
      // Not just `preview != null`: an expired preview still has a preview object, with both manifest
      // URLs null, and splitting null threw -- which errored this stream and blanked the whole page.
      // A cleared PO item (org switch, failed load) clears the link too.
      switchMap(poItem => previewManifestUrl(poItem?.preview) == null ? of(null) : of(poItem!).pipe(map(poItem => {
        let filename = poItem.filename;
        const url = previewManifestUrl(poItem.preview)!;
        if (filename.includes('/')) {
          const filename_parts = filename.split('/');
          filename = filename_parts[filename_parts.length - 1];
        }
        return url.split('/').slice(0, -1).join('/') + '/feed/' + filename + '.json';
      }),
      switchMap(url => this.http.get<any>(url).pipe(
        catchError(() => of(null)),
        map((res: any) => res ? url : null)
      )))),
      shareReplay(1)
    );

    this.flytedgeMetadataUrl = toSignal(flytedgeMetadataUrl$);
  }

  ngOnInit() {
    // Create breadcrumbs as a computed signal
    this.breadcrumbs = computed(() => {
      const poItem = this.poItem();
      const org = this.organization();
      const airline = this.airline();

      if (!poItem || !org || !airline) {
        return [];
      }

      const po_name = poItem.po.po_name;

      return [
        {
          display: 'Content Platform',
          commands: ['/o', org.id],
        },
        {
          display: 'Workflows',
          commands: ['/o', org.id, 'workflows'],
        },
        {
          display: airline.airline_name,
          commands: ['/o', org.id, 'workflows', airline.iata_code],
        },
        {
          display: po_name,
          commands: ['/o', org.id, 'workflows', airline.iata_code, po_name],
        }
      ];
    });
  }

  publish_single_po(poItem: PoItem) {
    // The item's own organization, captured now: the mutation must not pick up whatever organization()
    // says when it is sent.
    const organizationId = this.itemOrganizationId();
    if (!poItem || !organizationId || this.publishBlocked()) {
      return;
    }

    const action = () => {
      this.publish_single_po_inflight = true;
      this.apollo.use('workflow').mutate<WorkflowMutation>({
        mutation: PUBLISH_PO_ITEM,
        variables: {
          po_name: poItem.po.po_name,
          po_item_id: poItem.po_item_id,
        },
        context: {headers: new HttpHeaders({'X-Castlabs-Organization': organizationId})},
      }).pipe(
        tap((res) => {
          this.publish_single_po_inflight = false;
          const client = this.apollo.use('workflow').client;
          const cache = client.cache;
          cache.modify({
            id: cache.identify({__typename: 'POItem', id: poItem.id}),
            fields: {
              publish_process(existingPublishProcess, {toReference}) {
                return toReference(res.data.publishPoItem);
              },
            },
          });
        }),
        switchMap(d => this.processService.observeProcess(d.data.publishPoItem, {
          successMessage: `${poItem.po_item_id} published successfully.`,
          failureMessage: `${poItem.po_item_id} failed to publish.`,
          link: window.location.href
        })),
        skipWhile(process => process.state === ProcessStateEnum.InProgress)
      ).subscribe({
        error: (error) => {
          this.publish_single_po_inflight = false;
          this.banner.open(`Could not publish ${poItem.po_item_id}: ${errorText(error)}`, ['Close']);
        }
      });
    };

    if (poItem.publish_process &&
      (poItem.publish_process.state === ProcessStateEnum.InProgress ||
        poItem.publish_process.state === ProcessStateEnum.Success)) {
      let secondLine = '';
      if (poItem.publish_process.state === ProcessStateEnum.InProgress) {
        secondLine = 'This will restart an already running publish process.';
      }
      if (poItem.publish_process.state === ProcessStateEnum.Success) {
        secondLine = 'This will restart a completed publish process.';
      }
      this.dialog.open(ConfirmDialogComponent, {
        data: {
          headline: 'Publish',
          message: `Do you really want to publish ${poItem.po_item_id}?<br/>${secondLine}`,
          actionName: 'Publish',
          action: action
        }
      });
    } else {
      action();
    }
  }
}
